import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import api, { restoreAuthFromStorage } from "../api/Client";
import { API_BASE } from "../config";
import { SecureStorage } from "../storage/SecureStorage";
import "../styles/service-products.css";

type ServiceProduct = {
  id: number;
  name: string;
  description?: string | null;
  session?: number | null;
  price?: number | null;
  image?: string | null;
  type_of_product?: string | null;
  centro?: string | null;
  available_payment_methods?: string | null;
  center_id?: number | null;
  center_name?: string | null;
  center_slug?: string | null;
};

type UserProduct = {
  id: number;
  active_product_id?: number | null;
  type_of_product?: string | null;
};

type CenterOption = {
  id: number;
  name: string;
  slug: string;
};

type CheckoutPaymentMethod = "ewallet" | "card";

type LogicalKind = "Sesion" | "Bono" | "Suscripcion";

const EUR = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 2
});

function extractRequestError(error: unknown, fallback: string) {
  if (typeof error === "object" && error && "response" in error) {
    const response = error.response as { data?: { error?: string } } | undefined;
    if (response?.data?.error) {
      return response.data.error;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

function normalizeCenterSlug(value: string | null | undefined) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized || normalized === "todos" || normalized === "vip") {
    return null;
  }
  return normalized;
}

function formatCenterLabel(product: ServiceProduct) {
  if (product.center_name) {
    return product.center_name;
  }

  const normalized = normalizeCenterSlug(product.centro);
  if (normalized === "sotillo") {
    return "El Sotillo";
  }
  if (normalized === "guadarrama") {
    return "Guadarrama";
  }
  if (normalized) {
    return normalized;
  }

  return "A elegir";
}

function isSubscription(product: ServiceProduct) {
  return product.type_of_product === "recurrent";
}

function parseAvailablePaymentMethods(value: string | null | undefined) {
  const normalized = String(value || "")
    .toLowerCase()
    .split(/[,\s|;/]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

  if (normalized.length === 0) {
    return new Set<CheckoutPaymentMethod>(["ewallet"]);
  }

  const result = new Set<CheckoutPaymentMethod>();
  if (normalized.some((entry) => entry === "ewallet" || entry === "wallet")) {
    result.add("ewallet");
  }
  if (normalized.includes("card") || normalized.includes("stripe")) {
    result.add("card");
  }
  if (result.size === 0) {
    result.add("ewallet");
  }
  return result;
}

function productKind(product: ServiceProduct): LogicalKind {
  if (isSubscription(product)) {
    return "Suscripcion";
  }
  if (Number(product.session || 0) > 1) {
    return "Bono";
  }
  return "Sesion";
}

export default function ServiceProductsPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const serviceIdNumber = Number.parseInt(serviceId || "0", 10);

  const [products, setProducts] = useState<ServiceProduct[]>([]);
  const [userProducts, setUserProducts] = useState<UserProduct[]>([]);
  const [tenantCenters, setTenantCenters] = useState<CenterOption[]>([]);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [hasSavedCard, setHasSavedCard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedType, setSelectedType] = useState<"Todos" | LogicalKind>("Todos");
  const [selectedSession, setSelectedSession] = useState<string>("Todas");

  const [selectedProduct, setSelectedProduct] = useState<ServiceProduct | null>(null);
  const [selectedCenter, setSelectedCenter] = useState<string | null>(null);
  const [selectedCenterLabel, setSelectedCenterLabel] = useState<string | null>(null);
  const [showCenterModal, setShowCenterModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<CheckoutPaymentMethod>("ewallet");

  const [missingDni, setMissingDni] = useState(false);
  const [missingAddress, setMissingAddress] = useState(false);
  const [tempDni, setTempDni] = useState("");
  const [tempAddress, setTempAddress] = useState("");

  async function loadCatalog() {
    if (!serviceIdNumber || serviceIdNumber <= 0) {
      setError("Servicio no valido");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { user } = await restoreAuthFromStorage({ refreshSession: true });
      if (!user?.id) {
        navigate("/home", { replace: true });
        return;
      }

      const [catalogResponse, ownedResponse, centersResponse, walletResponse, cardsResponse] = await Promise.all([
        api.get<ServiceProduct[]>("/api/customers/catalog/service-products", {
          params: { primary_service_id: serviceIdNumber }
        }),
        api.get<UserProduct[]>("/api/customers/catalog/user-products"),
        api.get<CenterOption[]>("/api/customers/catalog/centers"),
        api.get<{ balance: number }>("/api/customers/wallet/balance"),
        api.get<{ hasData: boolean }>(`/api/customers/cards/has-data/${user.id}`)
      ]);

      setProducts(Array.isArray(catalogResponse.data) ? catalogResponse.data : []);
      setUserProducts(Array.isArray(ownedResponse.data) ? ownedResponse.data : []);
      setTenantCenters(Array.isArray(centersResponse.data) ? centersResponse.data : []);
      setWalletBalance(Number(walletResponse.data?.balance || 0));
      setHasSavedCard(Boolean(cardsResponse.data?.hasData));
    } catch (requestError) {
      setError(extractRequestError(requestError, "Error al cargar productos"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadCatalog();
  }, [serviceIdNumber]);

  const ownedProductIds = useMemo(
    () => new Set(userProducts.map((product) => Number(product.id))),
    [userProducts]
  );

  const ownedActiveProductIdByProductId = useMemo(() => {
    const map = new Map<number, number | null>();
    for (const product of userProducts) {
      map.set(Number(product.id), product.active_product_id ? Number(product.active_product_id) : null);
    }
    return map;
  }, [userProducts]);

  const typeOptions = useMemo(() => {
    const kinds = new Set<LogicalKind>();
    products.forEach((product) => kinds.add(productKind(product)));
    const ordered: LogicalKind[] = ["Sesion", "Bono", "Suscripcion"];
    return ["Todos", ...ordered.filter((kind) => kinds.has(kind))] as const;
  }, [products]);

  const sessionOptions = useMemo(() => {
    const sessions = products
      .map((product) => (product.session == null ? null : Number(product.session)))
      .filter((value): value is number => Number.isFinite(value) && value > 0);

    return Array.from(new Set(sessions)).sort((left, right) => left - right);
  }, [products]);

  const filteredProducts = useMemo(
    () =>
      products.filter((product) => {
        const matchesType = selectedType === "Todos" || productKind(product) === selectedType;
        const matchesSession = selectedSession === "Todas" || String(product.session || "") === selectedSession;
        return matchesType && matchesSession;
      }),
    [products, selectedSession, selectedType]
  );

  function resetPurchaseFlow() {
    setSelectedProduct(null);
    setSelectedCenter(null);
    setSelectedCenterLabel(null);
    setShowCenterModal(false);
    setShowPaymentModal(false);
    setShowUserInfoModal(false);
    setPurchaseError(null);
    setPurchasing(false);
    setSavingProfile(false);
    setSelectedPaymentMethod("ewallet");
    setMissingDni(false);
    setMissingAddress(false);
    setTempDni("");
    setTempAddress("");
  }

  function openOwnedProduct(productId: number) {
    const activeProductId = ownedActiveProductIdByProductId.get(productId);
    navigate(`/product/${productId}`, {
      state: activeProductId ? { activeProductId } : undefined
    });
  }

  function openPaymentForProduct(product: ServiceProduct, centerSlug: string | null, centerLabel: string | null) {
    const availableMethods = parseAvailablePaymentMethods(product.available_payment_methods);
    setSelectedProduct(product);
    setSelectedCenter(centerSlug);
    setSelectedCenterLabel(centerLabel);
    setSelectedPaymentMethod(availableMethods.has("ewallet") ? "ewallet" : "card");
    setPurchaseError(null);
    setShowCenterModal(false);
    setShowPaymentModal(true);
  }

  function startPurchaseFlow(product: ServiceProduct) {
    const directCenterSlug = product.center_slug || normalizeCenterSlug(product.centro);

    if (directCenterSlug) {
      openPaymentForProduct(product, directCenterSlug, formatCenterLabel(product));
      return;
    }

    if (tenantCenters.length === 1) {
      openPaymentForProduct(product, tenantCenters[0].slug || null, tenantCenters[0].name || null);
      return;
    }

    setSelectedProduct(product);
    setSelectedCenter(null);
    setSelectedCenterLabel(null);
    setPurchaseError(null);

    if (tenantCenters.length > 1) {
      setShowCenterModal(true);
      return;
    }

    setShowPaymentModal(true);
  }

  async function handleProductClick(product: ServiceProduct) {
    const isOwned = ownedProductIds.has(Number(product.id));

    if (isOwned) {
      if (isSubscription(product)) {
        openOwnedProduct(product.id);
        return;
      }

      const buyAnother = window.confirm("Ya tienes este bono activo. Quieres comprar otro igual?");
      if (!buyAnother) {
        openOwnedProduct(product.id);
        return;
      }
    }

    startPurchaseFlow(product);
  }

  async function ensureProfileInfo() {
    const user = await SecureStorage.getUser();
    const dni = String(user?.dni || "").trim();
    const address = String(user?.postAddress || user?.address || "").trim();
    const needsDni = dni.length === 0;
    const needsAddress = address.length === 0;

    if (!needsDni && !needsAddress) {
      return true;
    }

    setMissingDni(needsDni);
    setMissingAddress(needsAddress);
    setTempDni(dni);
    setTempAddress(address);
    setPurchaseError(null);
    setShowPaymentModal(false);
    setShowUserInfoModal(true);
    return false;
  }

  async function handleSaveUserInfo() {
    setPurchaseError(null);

    const normalizedDni = tempDni.trim().toUpperCase();
    const normalizedAddress = tempAddress.trim();

    if (missingDni && !/^(\d{8}[A-Z]|X\d{7}[A-Z])$/.test(normalizedDni)) {
      setPurchaseError("DNI invalido: debe tener 8 numeros y una letra mayuscula.");
      return;
    }

    if (missingAddress && !normalizedAddress) {
      setPurchaseError("La direccion postal es obligatoria.");
      return;
    }

    setSavingProfile(true);

    try {
      const user = await SecureStorage.getUser();
      if (!user?.id) {
        throw new Error("Usuario no disponible.");
      }

      await api.put("/api/customers/pay-info", {
        user_id: user.id,
        dni: normalizedDni || undefined,
        direccionPostal: normalizedAddress || undefined
      });

      await SecureStorage.saveUser({
        ...user,
        dni: normalizedDni || user.dni || "",
        postAddress: normalizedAddress || user.postAddress || user.address || ""
      });

      setShowUserInfoModal(false);
      setMissingDni(false);
      setMissingAddress(false);
      setTempDni("");
      setTempAddress("");

      await confirmPurchase(selectedPaymentMethod);
    } catch (requestError) {
      setPurchaseError(extractRequestError(requestError, "Error al guardar los datos"));
    } finally {
      setSavingProfile(false);
    }
  }

  async function confirmPurchase(paymentMethod: CheckoutPaymentMethod) {
    if (!selectedProduct || purchasing) {
      return;
    }

    const canContinue = await ensureProfileInfo();
    if (!canContinue) {
      return;
    }

    setPurchasing(true);
    setPurchaseError(null);

    try {
      const response = await api.post<{ assigned_id?: number }>("/api/customers/commerce/assign-product", {
        product_id: selectedProduct.id,
        payment_method: paymentMethod,
        centro: selectedCenter || undefined
      });

      const assignedId = Number(response.data?.assigned_id || 0) || undefined;
      const nextProductId = selectedProduct.id;

      resetPurchaseFlow();
      await loadCatalog();

      navigate(`/product/${nextProductId}`, {
        state: assignedId ? { activeProductId: assignedId } : undefined
      });
    } catch (requestError) {
      setPurchaseError(extractRequestError(requestError, "Error al contratar producto"));
    } finally {
      setPurchasing(false);
    }
  }

  return (
    <div className="service-products-page">
      {loading ? <p className="service-products-state">Cargando...</p> : null}
      {error ? <p className="service-products-error">{error}</p> : null}

      {!loading && !error ? (
        <>
          {products.length > 0 ? (
            <div className="service-products-filters">
              <div className="service-products-select-wrap">
                <select value={selectedType} onChange={(event) => setSelectedType(event.target.value as "Todos" | LogicalKind)}>
                  {typeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="service-products-select-wrap">
                <select value={selectedSession} onChange={(event) => setSelectedSession(event.target.value)}>
                  <option value="Todas">Todas las sesiones</option>
                  {sessionOptions.map((option) => (
                    <option key={option} value={String(option)}>
                      {option} sesiones
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}

          <div className="service-products-wallet">
            <span>Saldo en monedero</span>
            <strong>{EUR.format(walletBalance)}</strong>
          </div>

          {filteredProducts.length === 0 ? (
            <p className="service-products-empty">No hay productos disponibles para este servicio.</p>
          ) : null}

          {filteredProducts.map((product) => {
            const imageUrl =
              product.image && product.image !== "default.png"
                ? `${API_BASE}/api/product_images/${product.image}`
                : null;
            const isOwned = ownedProductIds.has(Number(product.id));

            return (
              <button
                key={product.id}
                type="button"
                className="service-products-card"
                onClick={() => {
                  void handleProductClick(product);
                }}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={String(product.name)}
                    className="service-products-card-image"
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="service-products-card-image service-products-card-image-fallback">
                    {String(product.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}

                <div className="service-products-card-main">
                  <div className="service-products-card-name">{String(product.name || "").toUpperCase()}</div>

                  {isOwned ? (
                    <div className="service-products-card-subtitle">Ya contratado</div>
                  ) : product.session != null ? (
                    <div className="service-products-card-subtitle">Sesiones: {product.session}</div>
                  ) : (
                    <div className="service-products-card-subtitle">{productKind(product)}</div>
                  )}

                  <div className="service-products-card-meta">
                    <span>{formatCenterLabel(product)}</span>
                    <span>{productKind(product)}</span>
                  </div>
                </div>

                <div className="service-products-card-price">
                  <strong>{EUR.format(Number(product.price || 0))}</strong>
                  <span>Monedero</span>
                </div>
              </button>
            );
          })}
        </>
      ) : null}

      {showCenterModal && selectedProduct ? (
        <div className="service-products-sheet-backdrop" onClick={() => setShowCenterModal(false)}>
          <div className="service-products-sheet" onClick={(event) => event.stopPropagation()}>
            <h4>Selecciona un centro</h4>

            <div className="service-products-sheet-list">
              {tenantCenters.map((center) => (
                <button
                  key={center.id}
                  type="button"
                  className="service-products-sheet-option"
                  onClick={() => openPaymentForProduct(selectedProduct, center.slug || null, center.name || null)}
                >
                  {center.name}
                </button>
              ))}

              <button type="button" className="service-products-sheet-cancel" onClick={() => resetPurchaseFlow()}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showPaymentModal && selectedProduct ? (
        <div className="service-products-sheet-backdrop" onClick={() => setShowPaymentModal(false)}>
          <div className="service-products-sheet" onClick={(event) => event.stopPropagation()}>
            <h4>Elige como pagar</h4>

            <div className="service-products-summary">
              <div className="service-products-summary-row">
                <span>Producto</span>
                <strong>{selectedProduct.name}</strong>
              </div>
              <div className="service-products-summary-row">
                <span>Centro</span>
                <strong>{selectedCenterLabel || "Sin centro especifico"}</strong>
              </div>
              <div className="service-products-summary-row">
                <span>Importe</span>
                <strong>{EUR.format(Number(selectedProduct.price || 0))}</strong>
              </div>
              <div className="service-products-summary-row">
                <span>Saldo actual</span>
                <strong>{EUR.format(walletBalance)}</strong>
              </div>
            </div>

            <div className="service-products-payment-methods">
              {parseAvailablePaymentMethods(selectedProduct.available_payment_methods).has("ewallet") ? (
                <button
                  type="button"
                  className={`service-products-method-chip ${selectedPaymentMethod === "ewallet" ? "active" : ""}`}
                  onClick={() => setSelectedPaymentMethod("ewallet")}
                >
                  Monedero
                </button>
              ) : null}

              {parseAvailablePaymentMethods(selectedProduct.available_payment_methods).has("card") ? (
                <button
                  type="button"
                  className={`service-products-method-chip ${selectedPaymentMethod === "card" ? "active" : ""}`}
                  onClick={() => setSelectedPaymentMethod("card")}
                >
                  Tarjeta
                </button>
              ) : null}
            </div>

            {selectedPaymentMethod === "ewallet" && walletBalance < Number(selectedProduct.price || 0) ? (
              <p className="service-products-warning">No tienes saldo suficiente en el monedero.</p>
            ) : null}

            {selectedPaymentMethod === "card" && !hasSavedCard ? (
              <p className="service-products-warning">
                No tienes una tarjeta guardada. Anadela primero en Metodos de pago.
              </p>
            ) : null}

            {purchaseError ? <p className="service-products-inline-error">{purchaseError}</p> : null}

            <div className="service-products-sheet-actions">
              <button
                type="button"
                className="service-products-primary"
                onClick={() => void confirmPurchase(selectedPaymentMethod)}
                disabled={
                  purchasing ||
                  (selectedPaymentMethod === "ewallet" && walletBalance < Number(selectedProduct.price || 0)) ||
                  (selectedPaymentMethod === "card" && !hasSavedCard)
                }
              >
                {purchasing
                  ? "Procesando..."
                  : selectedPaymentMethod === "card"
                    ? "Pagar con tarjeta"
                    : "Pagar con monedero"}
              </button>

              {selectedPaymentMethod === "card" && !hasSavedCard ? (
                <button
                  type="button"
                  className="service-products-secondary"
                  onClick={() => navigate("/payment-methods")}
                  disabled={purchasing}
                >
                  Ir a metodos de pago
                </button>
              ) : null}

              <button type="button" className="service-products-sheet-cancel" onClick={() => resetPurchaseFlow()} disabled={purchasing}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showUserInfoModal ? (
        <div className="service-products-modal-backdrop">
          <div className="service-products-modal">
            <h3>Completa tus datos antes de pagar</h3>

            {missingDni ? (
              <>
                <label>DNI *</label>
                <input
                  type="text"
                  placeholder="Introduce tu DNI"
                  value={tempDni}
                  onChange={(event) => setTempDni(event.target.value)}
                />
              </>
            ) : null}

            {missingAddress ? (
              <>
                <label>Direccion postal *</label>
                <input
                  type="text"
                  placeholder="Ej: Calle Real 123, Segovia"
                  value={tempAddress}
                  onChange={(event) => setTempAddress(event.target.value)}
                />
              </>
            ) : null}

            {purchaseError ? <p className="service-products-inline-error">{purchaseError}</p> : null}

            <button type="button" className="service-products-primary service-products-full-width" onClick={() => void handleSaveUserInfo()} disabled={savingProfile}>
              {savingProfile ? "Guardando..." : "Guardar y continuar"}
            </button>

            <button type="button" className="service-products-sheet-cancel service-products-full-width" onClick={() => resetPurchaseFlow()} disabled={savingProfile}>
              Cancelar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
